import { inject, Injectable } from '@angular/core';
import {
  AssetInstance,
  CreateAssetInstanceBatchParams,
  CreateAssetInstanceParams,
} from '../models/asset-instance.model';
import { compareBySerialWithTieBreaker } from '../utils/serial-sort';
import { AuthService } from './auth.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class AssetInstanceService {
  private db = inject(DatabaseService);
  private authService = inject(AuthService);
  private logger = inject(LoggerService).getLogger('AssetInstanceService');

  private parseRow(row: any): AssetInstance {
    return {
      id: row.id,
      asset_id: row.asset_id,
      serial_number: row.serial_number,
      is_deleted: row.is_deleted,
      data: row.data ? JSON.parse(row.data) : {},
      created_by: row.created_by,
      created_at: row.created_at,
      updated_by: row.updated_by,
      updated_at: row.updated_at,
    };
  }

  async getByCheckpointId(checkpointId: string): Promise<AssetInstance[]> {
    await this.db.init();
    const results = await this.db.exec(
      'SELECT * FROM asset_instances WHERE asset_id = ? AND is_deleted = 0',
      [checkpointId],
    );
    return results
      .map((row) => this.parseRow(row))
      .sort((a, b) => compareBySerialWithTieBreaker(a.serial_number, b.serial_number, a.id, b.id));
  }

  async getAllByCheckpointId(checkpointId: string): Promise<AssetInstance[]> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM asset_instances WHERE asset_id = ?', [
      checkpointId,
    ]);
    return results
      .map((row) => this.parseRow(row))
      .sort((a, b) => {
        if (a.is_deleted !== b.is_deleted) {
          return a.is_deleted - b.is_deleted;
        }
        return compareBySerialWithTieBreaker(a.serial_number, b.serial_number, a.id, b.id);
      });
  }

  createQueryByCheckpointId(checkpointId: string): QuerySignal<AssetInstance[]> {
    return this.db.createQuery('asset_instances', () => this.getByCheckpointId(checkpointId));
  }

  async getByCheckpointIds(checkpointIds: string[]): Promise<Map<string, AssetInstance[]>> {
    const map = new Map<string, AssetInstance[]>();
    if (checkpointIds.length === 0) return map;

    await this.db.init();
    const placeholders = checkpointIds.map(() => '?').join(',');
    const results = await this.db.exec(
      `SELECT * FROM asset_instances WHERE asset_id IN (${placeholders}) AND is_deleted = 0`,
      checkpointIds,
    );

    for (const row of results) {
      const instance = this.parseRow(row);
      const list = map.get(instance.asset_id) ?? [];
      list.push(instance);
      map.set(instance.asset_id, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => compareBySerialWithTieBreaker(a.serial_number, b.serial_number, a.id, b.id));
    }
    return map;
  }

  createQueryByCheckpointIds(checkpointIds: string[]): QuerySignal<Map<string, AssetInstance[]>> {
    return this.db.createQuery(
      'asset_instances',
      () => this.getByCheckpointIds(checkpointIds),
      new Map<string, AssetInstance[]>(),
    );
  }

  async getById(id: string): Promise<AssetInstance | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM asset_instances WHERE id = ?', [id]);
    return results.length > 0 ? this.parseRow(results[0]) : null;
  }

  async getCountByCheckpointId(checkpointId: string): Promise<number> {
    await this.db.init();
    const results = await this.db.exec(
      'SELECT COUNT(*) as count FROM asset_instances WHERE asset_id = ? AND is_deleted = 0',
      [checkpointId],
    );
    return results[0]?.count ?? 0;
  }

  async getNextSerialNumber(checkpointId: string): Promise<number> {
    await this.db.init();
    const results = await this.db.exec(
      `SELECT MAX(CAST(serial_number AS INTEGER)) as max_serial
       FROM asset_instances
       WHERE asset_id = ? AND is_deleted = 0`,
      [checkpointId],
    );
    return (results[0]?.max_serial ?? 0) + 1;
  }

  async validateSerialUnique(
    checkpointId: string,
    serialNumber: string,
    excludeId?: string,
  ): Promise<boolean> {
    await this.db.init();
    const query = excludeId
      ? 'SELECT id FROM asset_instances WHERE asset_id = ? AND serial_number = ? AND is_deleted = 0 AND id != ?'
      : 'SELECT id FROM asset_instances WHERE asset_id = ? AND serial_number = ? AND is_deleted = 0';
    const params = excludeId
      ? [checkpointId, serialNumber, excludeId]
      : [checkpointId, serialNumber];
    const results = await this.db.exec(query, params);
    return results.length === 0;
  }

  async create(params: CreateAssetInstanceParams): Promise<AssetInstance> {
    await this.db.init();
    const id = this.db.generateId();
    const userId = this.authService.getCurrentUserId() || '';
    const now = Date.now();

    await this.db.run(
      `INSERT INTO asset_instances (id, asset_id, serial_number, is_deleted, data, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, 0, ?, ?, ?, ?, ?)`,
      [
        id,
        params.asset_id,
        params.serial_number,
        JSON.stringify(params.data || {}),
        userId,
        now,
        userId,
        now,
      ],
    );

    const instance = await this.getById(id);
    if (!instance) {
      throw new Error('Failed to create asset instance');
    }
    return instance;
  }

  async createBatch(params: CreateAssetInstanceBatchParams): Promise<AssetInstance[]> {
    const instances: AssetInstance[] = [];
    for (let i = 0; i < params.count; i++) {
      const serialNumber = params.prefix
        ? `${params.prefix}${params.start_serial + i}`
        : String(params.start_serial + i);
      const instance = await this.create({
        asset_id: params.asset_id,
        serial_number: serialNumber,
      });
      instances.push(instance);
    }
    this.logger.debug(`Created ${instances.length} instances for checkpoint ${params.asset_id}`);
    return instances;
  }

  async update(
    id: string,
    params: { serial_number?: string; data?: Record<string, unknown> },
  ): Promise<AssetInstance> {
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) {
      throw new Error('Asset instance not found');
    }

    const updates: string[] = [];
    const values: any[] = [];

    if (params.serial_number !== undefined) {
      updates.push('serial_number = ?');
      values.push(params.serial_number);
    }
    if (params.data !== undefined) {
      updates.push('data = ?');
      values.push(JSON.stringify(params.data));
    }

    const userId = this.authService.getCurrentUserId() || '';
    updates.push('updated_by = ?');
    values.push(userId);
    updates.push('updated_at = ?');
    values.push(Date.now());
    values.push(id);

    await this.db.run(`UPDATE asset_instances SET ${updates.join(', ')} WHERE id = ?`, values);

    const updated = await this.getById(id);
    if (!updated) {
      throw new Error('Asset instance not found after update');
    }
    return updated;
  }

  async softDelete(id: string): Promise<void> {
    await this.db.init();
    const userId = this.authService.getCurrentUserId() || '';
    const now = Date.now();
    await this.db.run(
      'UPDATE asset_instances SET is_deleted = 1, updated_by = ?, updated_at = ? WHERE id = ?',
      [userId, now, id],
    );
  }

  async restore(id: string): Promise<void> {
    await this.db.init();
    const userId = this.authService.getCurrentUserId() || '';
    const now = Date.now();
    await this.db.run(
      'UPDATE asset_instances SET is_deleted = 0, updated_by = ?, updated_at = ? WHERE id = ?',
      [userId, now, id],
    );
  }

  async hasInstances(checkpointId: string): Promise<boolean> {
    const count = await this.getCountByCheckpointId(checkpointId);
    return count > 0;
  }
}
